/**
 * Reviews API — Supabase PostgREST queries.
 *
 * Schema (see migrations/001_schema.sql):
 *   reviews(id, author_id, subject_type, subject_id, rating, comment,
 *           is_public, status, created_at, updated_at)
 *   review_responses(id, review_id, author_id, message, created_at)
 *
 * Launch policy (US-11): reviews are an accountability tool, so they are
 * created public + approved (no moderation gate) and low ratings are NOT
 * hidden — locals can warn each other about a bad operator.
 */

import supabase from "../config/supabase";
import { invokeFunction } from "./functionErrors";

const reviewsApi = {
  // List reviews for a subject (e.g. a service listing) including author and
  // any owner responses.
  async list(params = {}) {
    let query = supabase
      .from("reviews")
      .select(
        "*, author:users!author_id(id, first_name, last_name, profile_image_url), responses:review_responses(id, message, author_id, created_at)",
      )
      .order("created_at", { ascending: false });

    if (params.subject_type) query = query.eq("subject_type", params.subject_type);
    if (params.subject_id) query = query.eq("subject_id", params.subject_id);
    if (params.author_id) query = query.eq("author_id", params.author_id);
    if (params.limit) query = query.limit(params.limit);

    const { data, error } = await query;
    if (error) return { success: false, error: error.message };
    return { success: true, data: data || [] };
  },

  async get(reviewId) {
    const { data, error } = await supabase
      .from("reviews")
      .select(
        "*, author:users!author_id(id, first_name, last_name, profile_image_url), responses:review_responses(id, message, author_id, created_at)",
      )
      .eq("id", reviewId)
      .single();
    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  // Create (or update) the current user's review for a subject. A unique
  // constraint allows one review per (author, subject), so we upsert to let a
  // resident correct/update their own review rather than erroring.
  async create(body) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const row = {
      author_id: user.id,
      subject_type: body.subject_type,
      subject_id: body.subject_id,
      rating: body.rating,
      comment: body.comment,
      is_public: true,
      status: "approved",
    };

    const existing = await this.getMine({
      subject_type: body.subject_type,
      subject_id: body.subject_id,
    });
    const replies = existing.success ? existing.data?.responses : null;
    if (replies?.length) {
      row.rating = existing.data.rating;
    }

    const { data, error } = await supabase
      .from("reviews")
      .upsert(row, { onConflict: "author_id,subject_type,subject_id" })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data, isUpdate: Boolean(existing.data) };
  },

  async getMine({ subject_type, subject_id } = {}) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };
    if (!subject_type || !subject_id) {
      return { success: false, error: "Missing subject" };
    }

    const { data, error } = await supabase
      .from("reviews")
      .select(
        "*, responses:review_responses(id, message, author_id, created_at)",
      )
      .eq("author_id", user.id)
      .eq("subject_type", subject_type)
      .eq("subject_id", subject_id)
      .maybeSingle();

    if (error) return { success: false, error: error.message };
    return { success: true, data: data || null };
  },

  // Change a review row. RLS (reviews_update_own) confines this to the author
  // and to moderators, so no role check is needed here.
  async update(reviewId, updates) {
    const { data, error } = await supabase
      .from("reviews")
      .update(updates)
      .eq("id", reviewId)
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },

  /**
   * Pull a published review back for moderation.
   *
   * Lands in Approvals under Reviews, the one screen where services, events,
   * jobs and claims already wait on a decision. Member-reported reviews go
   * somewhere else entirely (review_reports, read by Flagged Reviews); this is
   * a moderator reopening something already published, so it belongs with the
   * other things a moderator has to rule on.
   *
   * Hiding the review and queueing it happen in admin-actions, which holds the
   * service role: the queue insert policy requires submitted_by = auth.uid(),
   * and the submitter recorded on the card has to be the review's author.
   */
  async sendToModeration(reviewId) {
    return invokeFunction(
      supabase,
      "admin-actions",
      { body: { action: "queue_review", review_id: reviewId } },
      "Could not send that review to moderation. Please try again.",
    );
  },

  // Put a review back in public view. Used when a report is dismissed in
  // Flagged Reviews, which is the member-report queue and not Approvals.
  async restore(reviewId) {
    return this.update(reviewId, { status: "approved", is_public: true });
  },

  /**
   * Correct the wording of a review while it sits in Approvals.
   *
   * Two rows hold that wording and both have to move, which is the whole reason
   * this exists as one call rather than as an `update` at the call site:
   *
   *   reviews.comment                  what members read once it is published
   *   moderation_queue.content_snapshot what the Approvals card itself displays
   *
   * Saving only the review republishes correctly but leaves the moderator
   * reading the old text they have just rewritten. Saving only the snapshot
   * looks right in Approvals and then publishes the original wording — which is
   * exactly the bug this was written for.
   *
   * Both writes are the moderator's own, not the service role's: reviews_update_own
   * and moderation_update both admit is_moderator().
   */
  async editQueuedComment(reviewId, moderationId, comment) {
    const saved = await this.update(reviewId, { comment });
    if (!saved.success) return saved;

    // Queue rows are keyed separately from reviews, and a review reaching this
    // screen without one would be a bug elsewhere — but the correction itself
    // has already landed, so report success rather than inventing a failure.
    if (!moderationId) return saved;

    const { data: queued, error: readError } = await supabase
      .from("moderation_queue")
      .select("content_snapshot")
      .eq("id", moderationId)
      .single();

    if (readError) return { success: false, error: readError.message };

    // Merged, not replaced: the snapshot also carries the rating, the author's
    // name and the subject, and the card needs all of them.
    const { error: writeError } = await supabase
      .from("moderation_queue")
      .update({
        content_snapshot: { ...(queued?.content_snapshot || {}), comment },
      })
      .eq("id", moderationId);

    if (writeError) return { success: false, error: writeError.message };
    return saved;
  },

  // Owner/provider response to a review, stored in review_responses.
  async respond(reviewId, message) {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return { success: false, error: "Not authenticated" };

    const { data, error } = await supabase
      .from("review_responses")
      .insert({ review_id: reviewId, author_id: user.id, message })
      .select()
      .single();

    if (error) return { success: false, error: error.message };
    return { success: true, data };
  },
};

export default reviewsApi;
